/**
 * ProjectLeadersPanel — "configuración por proyecto" de la aprobación de compras
 *
 * QUÉ PIDIÓ JORGE (2026-07-28):
 *   "Toda compra de momento ocupa admin approve."
 *   "En configuración por proyecto el admin puede asignar ese permiso a un líder."
 *
 * La tabla `project_leaders` y la columna `can_approve_purchases` existían, y
 * las tools del agente también — pero NO había pantalla: el admin solo podía
 * delegar hablándole al agente. Esto es esa pantalla.
 *
 * LÍMITES A PROPÓSITO:
 *   - Este panel NO asigna ni quita líderes. Solo enciende/apaga el permiso
 *     sobre asignaciones que ya existen, que es exactamente lo que se pidió.
 *     Asignar sigue siendo `assign_project_leader` (admin o supervisor).
 *   - La UI no es la compuerta. Quien decide es la base
 *     (`trg_purchase_delegation_admin_only` + `can_approve_purchase()` +
 *     las policies de `purchases`) y, antes, la tool admin-only del
 *     mcp-server. Acá solo se decide qué se pinta; si esta pantalla se
 *     equivocara, la escritura igual se cae en el servidor.
 *
 * @module components/projects/ProjectLeadersPanel
 */

import React from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Card, Form, Spinner } from 'react-bootstrap';
import { toast } from '../../utils/toast';
import {
  listProjectLeaders,
  setProjectLeaderPurchaseApproval,
} from '../../services/projectLeaderService';
import { formatDate } from '../../utils/formatters';
import './ProjectLeadersPanel.css';

const ROLE_LABELS = {
  admin: 'Administrador',
  supervisor: 'Supervisor',
  technician: 'Técnico',
  employee: 'Colaborador',
  operario: 'Operario',
  mensajero: 'Mensajero',
  client: 'Cliente',
};

const ProjectLeadersPanel = ({ projectId, canManage = false }) => {
  const queryClient = useQueryClient();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['project-leaders', projectId],
    queryFn: async () => {
      const result = await listProjectLeaders(projectId);
      if (result.error) throw result.error;
      return result;
    },
    enabled: Boolean(projectId),
  });

  const leaders = data?.data || [];
  const delegationAvailable = data?.delegationAvailable ?? false;

  const toggleMutation = useMutation({
    mutationFn: async ({ userId, canApprovePurchases }) => {
      const result = await setProjectLeaderPurchaseApproval({
        projectId,
        userId,
        canApprovePurchases,
      });
      if (result.error) throw result.error;
      return result;
    },
    onSuccess: (result) => {
      // El mensaje lo redacta la tool (nombra a la persona y el efecto real).
      // Repetirlo acá sería una segunda versión de la misma frase, con el
      // riesgo habitual de que digan cosas distintas.
      toast.success(result.message || 'Permiso actualizado');
      queryClient.invalidateQueries({ queryKey: ['project-leaders', projectId] });
    },
    onError: (err) => {
      toast.error(err?.message || 'No se pudo actualizar el permiso');
    },
  });

  const pendingUserId = toggleMutation.isPending
    ? toggleMutation.variables?.userId
    : null;

  if (isLoading) {
    return (
      <Card className="shadow-sm">
        <Card.Body className="text-center py-4">
          <Spinner animation="border" size="sm" className="me-2" />
          Cargando líderes del proyecto…
        </Card.Body>
      </Card>
    );
  }

  if (isError) {
    return (
      <Alert variant="danger" className="mb-0">
        No se pudieron cargar los líderes del proyecto
        {error?.message ? `: ${error.message}` : '.'}
      </Alert>
    );
  }

  return (
    <Card className="shadow-sm project-leaders-panel">
      <Card.Header className="bg-white">
        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2">
          <h5 className="mb-0">
            <i className="bi bi-person-badge me-2"></i>
            Líderes del proyecto
          </h5>
          <Badge bg="light" text="dark" className="fw-normal">
            {leaders.length} asignado{leaders.length === 1 ? '' : 's'}
          </Badge>
        </div>
        <div className="text-muted mt-2" style={{ fontSize: '14px' }}>
          <strong>Las compras las aprueba únicamente el administrador.</strong> Esta
          marca ya no delega la aprobación: se conserva como registro de quién la
          tuvo cuando la delegación por proyecto estuvo vigente, y no habilita
          aprobar ninguna compra. Se pierde igual si la persona deja de ser líder.
        </div>
      </Card.Header>

      <Card.Body>
        {!delegationAvailable && (
          <Alert variant="warning" className="py-2" style={{ fontSize: '14px' }}>
            <i className="bi bi-exclamation-triangle me-2"></i>
            La base de datos de este entorno todavía no tiene la columna
            <code className="mx-1">project_leaders.can_approve_purchases</code>
            (migración <code>20260728120000_purchase_approval_delegation.sql</code>,
            sin aplicar). Se pueden ver los líderes, pero la delegación no se
            puede configurar hasta que esa migración esté aplicada.
          </Alert>
        )}

        {leaders.length === 0 ? (
          <div className="project-leaders-panel__empty">
            <i className="bi bi-people d-block mb-2" style={{ fontSize: '1.5rem' }}></i>
            Este proyecto no tiene líderes asignados todavía.
            <div className="mt-1">
              Primero hay que asignar a la persona como líder del proyecto; recién
              ahí se le puede delegar la aprobación de compras.
            </div>
          </div>
        ) : (
          <ul className="project-leaders-panel__list">
            {leaders.map((row) => {
              const name = row.leader?.full_name || 'Sin nombre';
              const roleLabel = ROLE_LABELS[row.leader?.role] || row.leader?.role || '—';
              const checked = Boolean(row.can_approve_purchases);
              const busy = pendingUserId === row.user_id;
              const switchId = `leader-approve-${row.id}`;

              return (
                <li key={row.id} className="project-leaders-panel__item">
                  <div className="project-leaders-panel__identity">
                    <span className="project-leaders-panel__name">{name}</span>
                    <span className="project-leaders-panel__meta">
                      {roleLabel}
                      {row.assigned_at && <> · Asignado {formatDate(row.assigned_at)}</>}
                      {row.assigned_by_profile?.full_name && (
                        <> · por {row.assigned_by_profile.full_name}</>
                      )}
                    </span>
                    {row.notes && (
                      <span className="project-leaders-panel__meta">{row.notes}</span>
                    )}
                  </div>

                  <div className="project-leaders-panel__control">
                    {canManage ? (
                      <Form.Check
                        type="switch"
                        id={switchId}
                        className="project-leaders-panel__switch"
                        label={
                          busy ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-2" />
                              Guardando…
                            </>
                          ) : (
                            'Puede aprobar compras'
                          )
                        }
                        checked={checked}
                        disabled={!delegationAvailable || toggleMutation.isPending}
                        onChange={(e) =>
                          toggleMutation.mutate({
                            userId: row.user_id,
                            canApprovePurchases: e.target.checked,
                          })
                        }
                      />
                    ) : (
                      <span className="project-leaders-panel__readonly">
                        {checked ? (
                          <Badge bg="success">Puede aprobar compras</Badge>
                        ) : (
                          <Badge bg="light" text="dark" className="fw-normal">
                            Sin permiso de aprobar compras
                          </Badge>
                        )}
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {!canManage && leaders.length > 0 && (
          <div className="text-muted mt-3" style={{ fontSize: '14px' }}>
            <i className="bi bi-lock me-2"></i>
            Solo un administrador puede cambiar quién aprueba las compras de un
            proyecto.
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

ProjectLeadersPanel.propTypes = {
  projectId: PropTypes.string.isRequired,
  /** Solo admin. La compuerta real está en el servidor; esto es la UI. */
  canManage: PropTypes.bool,
};

export default ProjectLeadersPanel;
